import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  automationConfigSchema,
  automationModelSchema,
  type Automation,
  type AutomationConfig,
  type AutomationScope,
  type ContentTag,
} from "@/lib/data/schemas/automations";
import {
  useSaveAutomation,
  capabilitiesQueryOptions,
} from "@/lib/data/react-query/automations";
import { AutomationScopeControls } from "./AutomationScopeControls";
import { automationError } from "./automationError";

export function AutomationEditor({
  automation,
  tags,
  onClose,
  initialScope = "firm",
  availableScopes = ["firm"],
}: {
  initialScope?: AutomationScope;
  availableScopes?: AutomationScope[];
  automation?: Automation;
  tags: ContentTag[];
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<AutomationConfig>(
    () =>
      automation ?? {
        model: "gpt-6-luna",
        scope: initialScope,
        uploadedByUserIds: [],
        name: "",
        instructions: "",
        tagIds: [],
        tagMatchMode: "any",
        capabilities: [],
        enabled: false,
      },
  );
  const [limitUploaders, setLimitUploaders] = useState(
    !!automation?.uploadedByUserIds.length,
  );
  const [validation, setValidation] = useState<string>();
  const [objectSearch, setObjectSearch] = useState("");
  const catalog = useQuery(capabilitiesQueryOptions());
  const save = useSaveAutomation(automation, onClose);
  const set = <K extends keyof AutomationConfig>(
    key: K,
    value: AutomationConfig[K],
  ) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };
  const knownTags = new Set(tags.map((tag) => tag.id));
  const missingTags = draft.tagIds.filter((id) => !knownTags.has(id));
  return (
    <form
      className="space-y-8"
      onSubmit={(event) => {
        event.preventDefault();
        const result = automationConfigSchema.safeParse(draft);
        if (!result.success) {
          setValidation(result.error.issues[0].message);
          return;
        }
        if (missingTags.length) {
          setValidation("Remove unavailable tags before saving.");
          return;
        }
        if (
          draft.scope === "firm" &&
          limitUploaders &&
          !draft.uploadedByUserIds.length
        ) {
          setValidation(
            "Choose at least one person, or allow anyone in the firm.",
          );
          return;
        }
        setValidation(undefined);
        save.mutate(result.data);
      }}
    >
      <div className="space-y-6">
        <label className="flex flex-col gap-3 font-medium">
          <span>Name</span>
          <Input
            autoFocus
            className="h-12 px-4 py-3 font-normal"
            value={draft.name}
            onChange={(event) => {
              set("name", event.target.value);
            }}
            placeholder="Organize medical records"
            maxLength={200}
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <span id="automation-model-label" className="text-sm font-medium">
            Model
          </span>
          <Select
            value={draft.model}
            onValueChange={(value) => {
              set("model", automationModelSchema.parse(value));
            }}
          >
            <SelectTrigger
              aria-labelledby="automation-model-label"
              className="h-9 w-fit gap-2 rounded-full px-3"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gpt-6-luna">GPT-6 Luna</SelectItem>
              <SelectItem value="gpt-6.1-sol">GPT-6.1 Sol</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <AutomationScopeControls
          scope={draft.scope}
          availableScopes={automation ? [automation.scope] : availableScopes}
          locked={!!automation}
          onScopeChange={(scope) => {
            setDraft((current) => ({
              ...current,
              scope,
              uploadedByUserIds: [],
            }));
            setLimitUploaders(false);
          }}
          limited={limitUploaders}
          onLimitedChange={(limited) => {
            setLimitUploaders(limited);
            if (!limited) set("uploadedByUserIds", []);
          }}
          uploaderIds={draft.uploadedByUserIds}
          onUploadersChange={(ids) => {
            set("uploadedByUserIds", ids);
          }}
        />
      </div>
      <div className="border-t pt-7">
        <fieldset className="space-y-4">
          <legend className="mb-4 text-base font-semibold">Trigger</legend>
          <label className="flex flex-col items-start gap-3 text-sm font-medium">
            <span>When a processed file has</span>
            <select
              aria-label="Tag matching"
              className="bg-background rounded-md border px-4 py-3 text-sm"
              value={draft.tagMatchMode}
              onChange={(event) => {
                set("tagMatchMode", event.target.value as "any" | "all");
              }}
            >
              <option value="any">Any of these tags</option>
              <option value="all">All of these tags</option>
            </select>
          </label>
          <div className="flex flex-wrap gap-3">
            {tags.map((tag) => (
              <label
                key={tag.id}
                title={tag.description}
                className="flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={draft.tagIds.includes(tag.id)}
                  onChange={(event) => {
                    set(
                      "tagIds",
                      event.target.checked
                        ? [...draft.tagIds, tag.id]
                        : draft.tagIds.filter((id) => id !== tag.id),
                    );
                  }}
                />
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
              </label>
            ))}
          </div>
          {!tags.length && (
            <p className="text-muted-foreground text-sm">
              Create a content tag first using Manage tags.
            </p>
          )}
          {!!missingTags.length && (
            <div role="alert" className="text-destructive text-sm">
              Some selected tags are unavailable.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => {
                  set(
                    "tagIds",
                    draft.tagIds.filter((id) => knownTags.has(id)),
                  );
                }}
              >
                Remove unavailable tags
              </button>
            </div>
          )}
        </fieldset>
      </div>
      <label className="flex flex-col gap-4 border-t pt-7 text-base font-semibold">
        <span>Instructions</span>
        <textarea
          className="bg-background min-h-40 w-full resize-y rounded-md border p-4 text-sm leading-relaxed font-normal"
          value={draft.instructions}
          onChange={(event) => {
            set("instructions", event.target.value);
          }}
          placeholder="Identify the providers in this file and create a note summarizing the treatment. Check for existing records before creating new ones."
        />
      </label>
      <div className="border-t pt-7">
        <fieldset className="space-y-4">
          <legend className="mb-4 text-base font-semibold">
            What can this automation do?
          </legend>
          <Input
            className="h-12 px-4 py-3"
            aria-label="Find an object"
            placeholder="Find an object…"
            value={objectSearch}
            onChange={(event) => {
              setObjectSearch(event.target.value);
            }}
          />
          {catalog.isPending && <p role="status">Loading available objects…</p>}
          {catalog.error && (
            <p role="alert">{automationError(catalog.error)}</p>
          )}
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="p-3 text-left">Object</th>
                  {["Create", "Update", "Delete"].map((action) => (
                    <th key={action} className="p-3 text-center">
                      {action}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {catalog.data
                  ?.filter((item) =>
                    item.label
                      .toLowerCase()
                      .includes(objectSearch.toLowerCase()),
                  )
                  .map((item) => (
                    <tr className="border-t" key={item.objectType}>
                      <td className="p-3">{item.label}</td>
                      {(["create", "update", "delete"] as const).map(
                        (action) => (
                          <td key={action} className="p-3 text-center">
                            {item.actions.includes(action) ? (
                              <input
                                type="checkbox"
                                aria-label={`${action} ${item.label}`}
                                checked={draft.capabilities.some(
                                  (permission) =>
                                    permission.objectType === item.objectType &&
                                    permission.action === action,
                                )}
                                onChange={(event) => {
                                  set(
                                    "capabilities",
                                    event.target.checked
                                      ? [
                                          ...draft.capabilities,
                                          {
                                            objectType: item.objectType,
                                            action,
                                          },
                                        ]
                                      : draft.capabilities.filter(
                                          (permission) =>
                                            permission.objectType !==
                                              item.objectType ||
                                            permission.action !== action,
                                        ),
                                  );
                                }}
                              />
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        ),
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </fieldset>
      </div>
      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) => {
            set("enabled", event.target.checked);
          }}
        />
        <span>Enable this automation</span>
      </label>
      {(validation || save.error) && (
        <p role="alert" className="text-destructive text-sm">
          {validation ?? automationError(save.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 border-t pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={save.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={save.isPending || catalog.isPending || !!catalog.error}
        >
          {save.isPending ? "Saving…" : "Save automation"}
        </Button>
      </div>
    </form>
  );
}
